import React, { useState, useEffect, useRef, Suspense } from "react";
import Modal from "../../../components/common/modal/modal";
import Button from "../../../components/common/button/button";
import Loader from "../../../components/common/loader/loader";
import OTPInput from "../../../components/common/otpInput/otpInput";
import DataRequest from "../../../services/dataRequest";
import { EmailVerifiedIcon, TickIcon, QuestionIcon } from "../../../assets/Icons";
import { getGlobalVal,getCookieValue, isValidUsPhoneNumber } from '../../../utilities/utils'
import { CACHE_CONSTANTS } from '../../../constants/cacheConstants';
import { CUSTOMER_NAME_CONFIG_TIMEOUT_MS, ZIP_CODE_REGEX } from '../../../constants/wcConstants';
import { getTermsUrl, getPrivacyPolicyUrl, getFamilyOfStoresConsentUrl } from '../../../constants/brandUrls';
import { useTranslation } from '../../../contexts/TranslationContext';
import "./otpmodalContent.scss";

async function fetchShowCustomerNameField(): Promise<boolean> {
  try {
    const response = await DataRequest.execute(
      "businessrule/rules/config/app/EZQuote",
      { method: "GET" },
      { "Content-Type": "application/json" }
    );
    return response?.data?.payload?.config?.enable_customerName === true;
  } catch (err) {
    console.error("Error fetching business rule config:", err);
    return false;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<T>((resolve) => {
    timeoutId = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

type OptionType = {
  key: string;
  value: string;
};

interface StoreOption extends OptionType {
  storeNumber: string;
  lat: number;
  lng: number;
  address: string;
  storename: string;
  brandName: string;
  distance: string | null;
}

type OtpModalContentProps = {
  isVisible: boolean;
  onClose: () => void;
  onVerified: (phoneNumber: string, zipCode: string, selectedStore: StoreOption | null, nearbyStores: StoreOption[], smsConsent: boolean, callConsent: boolean, customerName?: string) => void;
  brandName?: string;
  onValidateZipAndGetStores: (zipCode: string) => Promise<{ isValid: boolean; nearbyStores: StoreOption[]; selectedStore: StoreOption | null; }>;
  // Optional props for email mode
  mode?: "phone" | "email";
  quoteId?: string | null;
  items?: any[];
  totalEstimate?: { min: number; max: number };
  onEmailSent?: () => void;
};

type OtpFlowStep = "EMAIL" | "OTP" | "SUCCESS";

const OtpModalContent: React.FC<OtpModalContentProps> = ({
  isVisible,
  onClose,
  onVerified,
  brandName,
  onValidateZipAndGetStores,
  mode = "phone",
  quoteId = null,
  items: _items = [],
  totalEstimate: _totalEstimate = { min: 0, max: 0 },
  onEmailSent,
}) => {
  const t = useTranslation();

  const [otpStep, setOtpStep] = useState<OtpFlowStep>("EMAIL");

  const [userPhoneNumber, setUserPhoneNumber] = useState<string>("");
  const [userCustomerName, setUserCustomerName] = useState<string>("");
  const [showCustomerNameField, setShowCustomerNameField] = useState<boolean>(false);
  const [isCustomerNameConfigLoading, setIsCustomerNameConfigLoading] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [optIntoPromotions, setOptIntoPromotions] = useState<boolean>(true);
  const [contactConsent, setContactConsent] = useState<boolean>(false);
  const formattedPhoneNumber = `001${userPhoneNumber}`;

  const [otpValue, setOtpValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setResending] = useState<boolean>(false);
  const [errorMessage, setMessage] = useState<string>("");

  const [storedPhoneNumber, setStoredPhoneNumber] = useState(() => sessionStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.USER_EMAIL) || '');
  const [storedEmail, setStoredEmail] = useState(() => sessionStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.USER_EMAIL_COLLECT) || '');
  const [storedZipCode, setStoredZipCode] = useState(() => sessionStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.USER_ZIPCODE) || '');
  const [storedCustomerName, setStoredCustomerName] = useState(() => sessionStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.USER_NAME) || '');

  const [userZipCode, setUserZipCode] = useState<string>(storedZipCode);
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [selectedStoreInternal, setSelectedStoreInternal] = useState<StoreOption | null>(null);
  const [nearbyStoresInternal, setNearbyStoresInternal] = useState<StoreOption[]>([]);
  const [showZipCodeTooltip, setShowZipCodeTooltip] = useState<boolean>(false);
  const zipIconRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!showZipCodeTooltip) return;
    function handleClickOutside(event: MouseEvent) {
      if (zipIconRef.current && !zipIconRef.current.contains(event.target as Node)) {
        setShowZipCodeTooltip(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showZipCodeTooltip]);

  useEffect(() => {
    if (isVisible) {
      setOtpStep("EMAIL");
      if (mode === "email") {
        setUserEmail(storedEmail);
      } else {
        setUserPhoneNumber(storedPhoneNumber);
        setUserZipCode(storedZipCode);
        setContactConsent(false);
        setUserCustomerName(storedCustomerName);
      }
      setZipCodeError(null);
      setPhoneError(null);
      setOtpValue("");
      setMessage("");
      setIsLoading(false);
    }
  }, [isVisible, storedPhoneNumber, storedZipCode, storedCustomerName, storedEmail, mode]);

  useEffect(() => {
    if (!isVisible || mode !== "phone") return;

    let isCancelled = false;
    setIsCustomerNameConfigLoading(true);

    withTimeout(fetchShowCustomerNameField(), CUSTOMER_NAME_CONFIG_TIMEOUT_MS, false).then((show) => {
      if (isCancelled) return;
      setShowCustomerNameField(show);
      setIsCustomerNameConfigLoading(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [isVisible, mode]);

  useEffect(() => {
    if (mode === "phone" && otpStep === "OTP") {
      setTimeout(() => {
        const firstOtpInput = document.querySelector(".otp-input") as HTMLInputElement;
        if (firstOtpInput) {
          firstOtpInput.focus();
        }
      }, 300);
    }
  }, [otpStep]);

  useEffect(() => {
    if (otpValue.length === 4) {
      handleOtpAction("verify");
    }
  }, [otpValue]);

  const handleSendEmail = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userEmail)) {
        setMessage(t.otp.invalidEmail);
        setIsLoading(false);
        return;
      }

      sessionStorage.setItem(CACHE_CONSTANTS.CACHE_KEYS.USER_EMAIL_COLLECT, userEmail);
      setStoredEmail(userEmail);

      const payload = {
        email: userEmail,
        ezQuoteId: quoteId,
        emailConsent: optIntoPromotions,
        marketingConsent: optIntoPromotions,
        cookie: getCookieValue("__exponea_etc__")
      };

      const response = await DataRequest.execute(
        "quotation/email",
        {
          method: "POST",
          postData: JSON.stringify(payload),
        },
        { "Content-Type": "application/json" },
        true
      );

      window?.exponea?.identify({"email_id": userEmail}, {}, function() { }, function() { }, false);

      if (response?.data?.success) {
        setOtpStep("SUCCESS");
        if (onEmailSent) {
          onEmailSent();
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setMessage(response.data?.error?.message || t.otp.failedEmail);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setMessage(t.otp.somethingWrong);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpAction = async (action: "send" | "verify") => {
    setIsLoading(true);
    setMessage("");
    setZipCodeError(null);
    setPhoneError(null);
    try {
      let response;
      if (action === "send") {
        if (!isValidUsPhoneNumber(userPhoneNumber)) {
          setPhoneError(t.otp.phoneError);
          setIsLoading(false);
          return;
        }

        if (!ZIP_CODE_REGEX.test(userZipCode)) {
          setZipCodeError(t.otp.zipError5Digit);
          setIsLoading(false);
          return;
        }

        const validationResult = await onValidateZipAndGetStores(userZipCode);
        if (!validationResult || !validationResult.isValid) {
          setZipCodeError(t.otp.zipErrorInvalid);
          setIsLoading(false);
          return;
        }
        setSelectedStoreInternal(validationResult.selectedStore);
        setNearbyStoresInternal(validationResult.nearbyStores);

        sessionStorage.setItem(CACHE_CONSTANTS.CACHE_KEYS.USER_EMAIL, userPhoneNumber);
        sessionStorage.setItem(CACHE_CONSTANTS.CACHE_KEYS.USER_ZIPCODE, userZipCode);
        sessionStorage.setItem(CACHE_CONSTANTS.CACHE_KEYS.USER_NAME, userCustomerName);
        setStoredPhoneNumber(userPhoneNumber);
        setStoredZipCode(userZipCode);
        setStoredCustomerName(userCustomerName);

        if (Number(getGlobalVal('VERIFY_EMAIL_OTP')) === 0) {
          handleDone(validationResult.selectedStore, validationResult.nearbyStores);
          setIsLoading(false);
          return;
        }

        response = await DataRequest.execute(
          "auth/otp",
          {
            method: "POST",
            postData: JSON.stringify({
              phoneNumber: formattedPhoneNumber,
              location: validationResult.selectedStore?.storeNumber || "",
              brand: brandName,
              email_marketing: optIntoPromotions,
              zip_code: userZipCode,
            }),
          },
          { "Content-Type": "application/json" }
        );

        if (!response.data?.success) {
          setMessage(response.data?.messages?.[0] || t.otp.failedOtp);
          setIsLoading(false);
          return;
        }
        setOtpStep("OTP");
      } else if (action === "verify") {
        setMessage(t.otp.verifying);
        response = await DataRequest.execute("auth/otp/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          postData: JSON.stringify({ phoneNumber: formattedPhoneNumber, otp: otpValue }),
        });

        if (!response.data?.success) {
          setMessage(response.data?.messages?.[0] || t.otp.invalidOtp);
          setIsLoading(false);
          return;
        }
        setMessage("");
        setOtpStep("SUCCESS");
        setTimeout(() => {
          handleDone(selectedStoreInternal, nearbyStoresInternal);
          onClose();
        }, 1000);
      }
    } catch (err) {
      console.error("Error during OTP action:", err);
      setMessage(action === "send" ? t.otp.failedOtp : t.otp.invalidOtp);
      setIsLoading(false);
    } finally {
      setResending(false);
    }
  };

  const handleDone = (selectedStore: StoreOption | null, nearbyStores: StoreOption[]) => {
    onVerified(formattedPhoneNumber, userZipCode, selectedStore, nearbyStores, contactConsent, contactConsent, showCustomerNameField ? userCustomerName.trim() : undefined);
  };

  if (!isVisible) return null;

  const brandUpper = brandName?.toLocaleUpperCase() || 'EZPAWN';
  // Phone/SMS consent copy: a brand's legal-approved override if it has one,
  // otherwise the default copy.
  const consentCopy = t.otp.consentByBrand?.[brandName?.toLowerCase() ?? ''] ?? t.otp;

  // Phone mode: full-screen, no Modal wrapper
  if (mode === "phone") {
    return (
      <div className="otp-content-wrapper otp-phone-fullscreen">
        {isCustomerNameConfigLoading ? (
          <Loader />
        ) : otpStep === "EMAIL" && (
          <div className="otp-step">
            <div className="otp-title">{t.otp.almostThere}</div>
            <div className="otp-title-description">{t.otp.quotesDeliveredByText}</div>

            {showCustomerNameField && (
              <>
                <label htmlFor="otpCustomerNameInput" className="email-label">
                  {t.otp.customerName}<span className="required-asterisk">*</span>
                </label>
                <div className="email-input-wrapper">
                  <div className="input-container">
                    <input
                      id="otpCustomerNameInput"
                      type="text"
                      value={userCustomerName}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\p{L}\s'-]/gu, '');
                        setUserCustomerName(value);
                        setMessage('');
                      }}
                      className="email-input"
                      maxLength={50}
                    />
                  </div>
                </div>
              </>
            )}

            <label htmlFor="otpPhoneInput" className="email-label">
              {t.otp.phoneNumber}<span className="required-asterisk">*</span>
            </label>
            <div className="email-input-wrapper">
              <div className="input-container">
                <input
                  id="otpPhoneInput"
                  type="tel"
                  placeholder={t.otp.phonePlaceholder}
                  value={userPhoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setUserPhoneNumber(value);
                    setMessage('');
                    setPhoneError(null);
                  }}
                  onBlur={() => {
                    if (userPhoneNumber && !isValidUsPhoneNumber(userPhoneNumber)) {
                      setPhoneError(t.otp.phoneError);
                    }
                  }}
                  className="email-input"
                  maxLength={10}
                />
              </div>
              {phoneError && (
                <div className="phone-error-container">
                  <p className="error-message">{phoneError}</p>
                </div>
              )}
              <div className="phone-info">{t.otp.phoneInfo}</div>
            </div>

            <label htmlFor="otpZipCodeInput" className="email-label">
              {t.otp.zipCode}<span className="required-asterisk">*</span>
              <span
                className="label-icon"
                ref={zipIconRef}
                onMouseEnter={() => setShowZipCodeTooltip(true)}
                onMouseLeave={() => setShowZipCodeTooltip(false)}
                onClick={() => setShowZipCodeTooltip((prev) => !prev)}
                tabIndex={0}
              >
                <Suspense fallback={null}>
                  <QuestionIcon width={20} height={20} />
                </Suspense>
                {showZipCodeTooltip && (
                  <div className="zip-code-tooltip">{t.otp.zipTooltip}</div>
                )}
              </span>
            </label>
            <div className="email-input-wrapper">
              <div className="input-container">
                <input
                  id="otpZipCodeInput"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={t.otp.zipPlaceholder}
                  value={userZipCode}
                  onChange={(e) => {
                    setUserZipCode(e.target.value);
                    setZipCodeError(null);
                  }}
                  onBlur={() => {
                    if (userZipCode && !ZIP_CODE_REGEX.test(userZipCode)) {
                      setZipCodeError(t.otp.zipError5Digit);
                    }
                  }}
                  className="email-input"
                  maxLength={5}
                />
              </div>
              {zipCodeError && (
                <div className="zip-code-error-container">
                  <p className="error-message">{zipCodeError}</p>
                </div>
              )}
            </div>

            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <div className="consent-checkbox">
              <input
                id="contactConsentCheckbox"
                type="checkbox"
                checked={contactConsent}
                onChange={(e) => setContactConsent(e.target.checked)}
              />
              <div className="consent-checkbox-text">
                <label htmlFor="contactConsentCheckbox" className="consent-checkbox-label">
                  {consentCopy.consentLabel}<span className="consent-required-star">*</span>
                </label>
                <p className="consent-checkbox-description">
                  {consentCopy.consentPrefix(brandUpper)}
                  <a href={getFamilyOfStoresConsentUrl()} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>{consentCopy.consentEzPlusStoresLink}</a>
                  {consentCopy.consentMiddle}
                  <a href={getPrivacyPolicyUrl(brandName)} target="_blank" rel="noreferrer">{consentCopy.privacyPolicyLink}</a>
                  {' '}{consentCopy.consentAnd}{' '}
                  <a href={getTermsUrl(brandName)} target="_blank" rel="noreferrer">{consentCopy.termsLink}</a>
                  {consentCopy.consentPost}
                </p>
              </div>
            </div>

            <Button
              label={isLoading ? t.otp.sending : t.shared.continue}
              onClick={() => { handleOtpAction("send"); }}
              styleType="primary"
              disabled={userPhoneNumber.length !== 10 || !!phoneError || !userZipCode || !!zipCodeError || isLoading || !contactConsent || isCustomerNameConfigLoading || (showCustomerNameField && !userCustomerName.trim())}
            />
          </div>
        )}

        {(otpStep === "OTP" || otpStep === "SUCCESS") && (
          <div className="otp-step">
            <div className="otp-title">{t.otp.verifyTitle}</div>
            <div className="otp-title-description">{t.otp.enterCode}</div>
            <div className="otp-email-value">{userPhoneNumber}</div>
            {otpStep === "OTP" && (
              <>
                <OTPInput key="otp" length={4} onChange={setOtpValue} />
                <div className="resend-otp">
                  <div className="not-get-code">{t.otp.didntGetCode}</div>
                  <button
                    type="button"
                    onClick={() => {
                      handleOtpAction("send");
                      setResending(true);
                    }}
                    className="resend-link"
                    disabled={isLoading || isResending}
                  >
                    {isResending ? t.otp.resending : t.otp.resendCode}
                  </button>
                </div>
              </>
            )}

            {otpStep === "SUCCESS" && (
              <div className="otp-success-message">
                <Suspense fallback={null}>
                  <EmailVerifiedIcon key={brandName || 'default'} className="email-verified-icon" />
                </Suspense>
                <div className="verified-text">{t.otp.phoneVerified}</div>
              </div>
            )}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
          </div>
        )}
      </div>
    );
  }

  // Email mode: Modal wrapper
  return (
    <Modal isOpen={isVisible} onClose={onClose} title="" className="otp-modal">
      <div className="otp-content-wrapper">
        {(otpStep === "EMAIL" || otpStep === "SUCCESS") && (
          <div className="otp-step">
            <div className="otp-title">{t.otp.emailTitle}</div>
            <div className="otp-title-description">{t.otp.emailDescription}</div>

            <label htmlFor="emailInput" className="email-label">
              {t.otp.emailLabel}<span className="required-asterisk">*</span>
            </label>
            <div className="email-input-wrapper">
              <div className="input-container">
                <input
                  id="emailInput"
                  type="email"
                  placeholder={t.otp.emailPlaceholder}
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                    setMessage("");
                  }}
                  className="email-input"
                />
              </div>
            </div>

            <div className="promotions-optin">
              <input
                id="promotionsCheckbox"
                type="checkbox"
                checked={optIntoPromotions}
                onChange={(e) => setOptIntoPromotions(e.target.checked)}
              />
              <div className="opt-email">{t.otp.optInPromotions}</div>
            </div>
            <div className="promotions-text">
              {t.otp.promotionsTextPrefix}
              <a href={getFamilyOfStoresConsentUrl()} target="_blank" rel="noreferrer" style={{ color: "var(--ez-color-primary)", textDecoration: "none" }}>{t.otp.promotionsTextLink}</a>
              {t.otp.promotionsTextSuffix}
              <a href={getTermsUrl(brandName)} target="_blank" rel="noreferrer">{t.otp.termsOfUse}</a>
              {' '}{t.otp.tncMid}{' '}
              <a href={getPrivacyPolicyUrl(brandName)} target="_blank" rel="noreferrer">{t.otp.privacyPolicy}</a>
            </div>

            {errorMessage && <p className="error-message">{errorMessage}</p>}

            {otpStep === "SUCCESS" ? (
              <Button
                label={t.otp.emailSent}
                icon={
                  <Suspense fallback={null}>
                    <TickIcon key={brandName || 'default'} className="tick-icon" color="#FFFFFF" />
                  </Suspense>
                }
                styleType="primary"
                disabled={true}
                className="email-sent-button"
              />
            ) : (
              <Button
                id="send-email-quotation-copy"
                label={isLoading ? t.otp.sending : t.otp.send}
                onClick={handleSendEmail}
                styleType="primary"
                disabled={!userEmail || isLoading}
              />
            )}
            <div className="tnc">
              {t.otp.tncPre}{" "}
              <a href={getTermsUrl(brandName)} target="_blank" rel="noreferrer">
                {t.otp.termsOfUse}
              </a>{" "}
              {t.otp.tncMid}{" "}
              <a href={getPrivacyPolicyUrl(brandName)} target="_blank" rel="noreferrer">
                {t.otp.privacyPolicy}
              </a>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OtpModalContent;
